package com.daytimegaming.ourdailyfinances.presentation.account

import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.presentation.util.CATEGORIES
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.presentation.preview.TransactionDetailPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import com.daytimegaming.ourdailyfinances.presentation.util.toCurrencySymbol
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf

@Composable
fun TransactionDetailScreen(
    transactionId: String,
    dashboardId: String? = null,
    onNavigateBack: () -> Unit,
    onInvalidAccess: (() -> Unit)? = null,
    viewModel: TransactionDetailViewModel = koinViewModel(key = transactionId) { parametersOf(transactionId, dashboardId) }
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    LaunchedEffect(state) {
        if (state is TransactionDetailScreenState.Error && onInvalidAccess != null) {
            onInvalidAccess()
        }
    }

    TransactionDetailScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onUpdateCategory = { detailedCategory ->
            viewModel.updateCategory(detailedCategory)
        }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TransactionDetailScreenContent(
    state: TransactionDetailScreenState,
    onNavigateBack: () -> Unit,
    onUpdateCategory: (String?) -> Unit = {}
) {
    Scaffold(
        modifier = Modifier.fillMaxSize(),
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = when (state) {
                            is TransactionDetailScreenState.Loaded -> state.transaction.merchantName ?: state.transaction.name
                            else -> "Transaction Details"
                        },
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold
                    )
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                }
            )
        }
    ) { contentPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(contentPadding)
        ) {
            when (state) {
                is TransactionDetailScreenState.Loading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator()
                    }
                }
                is TransactionDetailScreenState.Error -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = state.message,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }
                is TransactionDetailScreenState.Loaded -> {
                    TransactionDetailContent(
                        transaction = state.transaction,
                        onUpdateCategory = onUpdateCategory
                    )
                }
            }
        }
    }
}

@Composable
private fun TransactionDetailContent(
    transaction: Transaction,
    onUpdateCategory: (String?) -> Unit
) {
    val currencySymbol = transaction.isoCurrencyCode.toCurrencySymbol()
    val isNegative = transaction.amount < 0
    val absAmount = kotlin.math.abs(transaction.amount)
    val formattedAmount = absAmount.formatAmount()
    val parts = formattedAmount.split('.')
    val intPart = parts.getOrElse(0) { "0" }
    val decPart = parts.getOrNull(1) ?: "00"

    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        // Large Amount Display
        item {
            Column(
                modifier = Modifier
                    .fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    text = if (isNegative) "CREDIT (MONEY IN)" else "DEBIT (MONEY OUT)",
                    style = MaterialTheme.typography.labelSmall.copy(letterSpacing = 2.sp),
                    color = if (isNegative) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(8.dp))
                Row(
                    verticalAlignment = Alignment.Bottom
                ) {
                    Text(
                        text = if (isNegative) "+" else "-",
                        style = MaterialTheme.typography.titleLarge,
                        color = if (isNegative) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.error,
                        modifier = Modifier.padding(bottom = 12.dp)
                    )
                    Text(
                        text = currencySymbol,
                        style = MaterialTheme.typography.titleLarge,
                        color = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.padding(bottom = 12.dp)
                    )
                    Text(
                        text = intPart,
                        style = MaterialTheme.typography.displayLarge.copy(fontSize = 48.sp),
                        color = MaterialTheme.colorScheme.onSurface,
                        fontWeight = FontWeight.Bold
                    )
                    Text(
                        text = ".$decPart",
                        style = MaterialTheme.typography.titleLarge,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(bottom = 8.dp)
                    )
                }
                Spacer(modifier = Modifier.height(12.dp))
                // Status badge
                Row {
                    Box(
                        modifier = Modifier
                            .background(
                                color = if (transaction.pending) {
                                    MaterialTheme.colorScheme.errorContainer.copy(alpha = 0.2f)
                                } else {
                                    MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.2f)
                                },
                                shape = RoundedCornerShape(999.dp)
                            )
                            .padding(horizontal = 12.dp, vertical = 6.dp)
                    ) {
                        Text(
                            text = if (transaction.pending) "PENDING" else "POSTED / CLEARED",
                            style = MaterialTheme.typography.labelMedium,
                            color = if (transaction.pending) {
                                MaterialTheme.colorScheme.error
                            } else {
                                MaterialTheme.colorScheme.primary
                            },
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
                Spacer(modifier = Modifier.height(8.dp))
            }
        }

        // Basic Information Card
        item {
            CardSection(title = "Transaction Details") {
                DetailRow(label = "Date", value = transaction.date)
                DetailRow(label = "Full Name", value = transaction.name)
                transaction.merchantName?.let {
                    DetailRow(label = "Merchant Name", value = it)
                }
                DetailRow(label = "Transaction ID", value = transaction.transactionId)
                DetailRow(label = "Account ID", value = transaction.accountId)
                transaction.website?.let {
                    DetailRow(label = "Website", value = it)
                }
            }
        }

        // Category Card
        item {
            var showDialog by remember { mutableStateOf(false) }

            CardSection(
                title = "Categories",
                action = {
                    IconButton(onClick = { showDialog = true }) {
                        Icon(
                            imageVector = Icons.Default.Settings,
                            contentDescription = "Configure Category",
                            tint = MaterialTheme.colorScheme.primary
                        )
                    }
                }
            ) {
                DetailRow(label = "Tags", value = transaction.category.joinToString(" / "))
                transaction.personalFinanceCategory?.let { pfc ->
                    DetailRow(label = "Primary Category", value = pfc.primary.replace("_", " "))
                    DetailRow(label = "Detailed Category", value = pfc.detailed.replace("_", " "))
                    pfc.confidenceLevel?.let {
                        DetailRow(label = "Confidence Level", value = it)
                    }
                    pfc.version?.let {
                        DetailRow(label = "Category Version", value = it)
                    }
                }
                transaction.userCategoryOverride?.let { uco ->
                    DetailRow(label = "User Override (Primary)", value = uco.primary.replace("_", " "))
                    DetailRow(label = "User Override (Detailed)", value = uco.detailed.replace("_", " "))
                }
            }

            if (showDialog) {
                UpdateCategoryDialog(
                    initialPrimary = transaction.activeCategory?.primary ?: "",
                    initialDetailed = transaction.activeCategory?.detailed ?: "",
                    onDismiss = { showDialog = false },
                    onConfirm = { detailed ->
                        onUpdateCategory(detailed)
                        showDialog = false
                    }
                )
            }
        }

        // Location Card if present
        transaction.location?.let { loc ->
            if (loc.address != null || loc.city != null || loc.lat != null || loc.storeNumber != null) {
                item {
                    CardSection(title = "Location") {
                        loc.storeNumber?.let { DetailRow(label = "Store Number", value = it) }
                        loc.address?.let { DetailRow(label = "Address", value = it) }
                        loc.city?.let { DetailRow(label = "City", value = it) }
                        loc.region?.let { DetailRow(label = "Region", value = it) }
                        loc.postalCode?.let { DetailRow(label = "Postal Code", value = it) }
                        loc.country?.let { DetailRow(label = "Country", value = it) }
                        if (loc.lat != null && loc.lon != null) {
                            DetailRow(label = "Coordinates", value = "${loc.lat}, ${loc.lon}")
                        }
                    }
                }
            }
        }

        // Payment Metadata Card if present
        transaction.paymentMeta?.let { pm ->
            if (pm.referenceNumber != null || pm.paymentMethod != null || pm.payee != null) {
                item {
                    CardSection(title = "Payment Metadata") {
                        pm.paymentMethod?.let { DetailRow(label = "Payment Method", value = it) }
                        pm.payee?.let { DetailRow(label = "Payee", value = it) }
                        pm.payer?.let { DetailRow(label = "Payer", value = it) }
                        pm.paymentProcessor?.let { DetailRow(label = "Processor", value = it) }
                        pm.referenceNumber?.let { DetailRow(label = "Reference Number", value = it) }
                        pm.ppdId?.let { DetailRow(label = "PPD ID", value = it) }
                        pm.byOrderOf?.let { DetailRow(label = "By Order Of", value = it) }
                        pm.reason?.let { DetailRow(label = "Reason", value = it) }
                    }
                }
            }
        }

        // Counterparties Card if present
        transaction.counterparties?.let { counterparties ->
            if (counterparties.isNotEmpty()) {
                item {
                    CardSection(title = "Counterparties") {
                        counterparties.forEachIndexed { idx, cp ->
                            if (idx > 0) {
                                Spacer(modifier = Modifier.height(12.dp))
                            }
                            Text(
                                text = "Counterparty #${idx + 1}",
                                style = MaterialTheme.typography.bodyMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.secondary
                            )
                            DetailRow(label = "Name", value = cp.name)
                            DetailRow(label = "Type", value = cp.type)
                            cp.website?.let { DetailRow(label = "Website", value = it) }
                            cp.entityId?.let { DetailRow(label = "Entity ID", value = it) }
                            cp.confidenceLevel?.let { DetailRow(label = "Confidence Level", value = it) }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CardSection(
    title: String,
    action: (@Composable () -> Unit)? = null,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.padding(start = 4.dp, bottom = 8.dp)
            )
            action?.let {
                Box(modifier = Modifier.padding(bottom = 8.dp)) {
                    it()
                }
            }
        }
        GlassCard(modifier = Modifier.fillMaxWidth()) {
            Column(
                modifier = Modifier
                    .fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                ColumnScope().content()
            }
        }
    }
}

// A helper wrapper to get a ColumnScope context for CardSection content
private class ColumnScope

@Composable
private fun DetailRow(
    label: String,
    value: String
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurface
        )
    }
}

@PreviewLightDark
@Composable
fun TransactionDetailScreenPreview(
    @PreviewParameter(TransactionDetailPreviewParameterProvider::class) state: TransactionDetailScreenState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
            TransactionDetailScreenContent(
                state = state,
                onNavigateBack = {}
            )
        }
    }
}

@Composable
fun UpdateCategoryDialog(
    initialPrimary: String,
    initialDetailed: String,
    onDismiss: () -> Unit,
    onConfirm: (String) -> Unit
) {
    val primaries = remember { CATEGORIES.map { it.first }.distinct() }
    
    val startPrimary = if (initialPrimary.isNotEmpty() && initialPrimary in primaries) {
        initialPrimary
    } else {
        primaries.firstOrNull() ?: ""
    }
    
    var selectedPrimary by remember { mutableStateOf(startPrimary) }
    
    val detailedOptions = remember(selectedPrimary) {
        CATEGORIES.filter { it.first == selectedPrimary }.map { it.second }
    }
    
    var selectedDetailed by remember { 
        mutableStateOf(
            if (initialDetailed.isNotEmpty() && initialDetailed in detailedOptions) {
                initialDetailed
            } else {
                detailedOptions.firstOrNull() ?: ""
            }
        )
    }
    
    val onPrimarySelected: (String) -> Unit = { primary ->
        selectedPrimary = primary
        selectedDetailed = CATEGORIES.firstOrNull { it.first == primary }?.second ?: ""
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                text = "Update Transaction Category",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold
            )
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                CategoryDropdown(
                    label = "Primary Category",
                    selectedOption = selectedPrimary,
                    options = primaries,
                    onOptionSelected = onPrimarySelected
                )
                
                CategoryDropdown(
                    label = "Detailed Category",
                    selectedOption = selectedDetailed,
                    options = detailedOptions,
                    onOptionSelected = { selectedDetailed = it }
                )
            }
        },
        confirmButton = {
            TextButton(
                onClick = {
                    onConfirm(selectedDetailed)
                }
            ) {
                Text("Update Category")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CategoryDropdown(
    label: String,
    selectedOption: String,
    options: List<String>,
    onOptionSelected: (String) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    
    Column(modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)) {
        Text(
            text = label,
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
        Spacer(modifier = Modifier.height(4.dp))
        Box(modifier = Modifier.fillMaxWidth()) {
            OutlinedButton(
                onClick = { expanded = true },
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(8.dp)
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = selectedOption.replace("_", " "),
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    Icon(
                        imageVector = Icons.Default.ArrowDropDown,
                        contentDescription = "Dropdown Arrow",
                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            DropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false },
                modifier = Modifier.fillMaxWidth(0.9f)
            ) {
                options.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(text = option.replace("_", " ")) },
                        onClick = {
                            onOptionSelected(option)
                            expanded = false
                        }
                    )
                }
            }
        }
    }
}
